import { z } from 'zod';
import { objectId } from './schemas.js';
/**
 * What people are paid, and the run that pays them.
 *
 * This is the most sensitive data the workspace holds, and the module is
 * shaped around that rather than around payroll mechanics. Two rules do most
 * of the work: every figure here is behind `payroll:view`, which nobody holds
 * by being an administrator; and the one exception — a person's own payslips —
 * is served by its own endpoints that never take a user id, so there is no
 * route where passing somebody else's id is even expressible.
 *
 * It does not file taxes, and does not pretend to. It records what was agreed,
 * computes what that comes to for a period, and keeps the payslip. The
 * transfer itself happens at a bank, and `paid` is somebody saying it did.
 */
export const PAY_FREQUENCIES = ['monthly', 'semi-monthly', 'biweekly', 'weekly'];
/** How many pay periods a year each frequency has, for the per-period figure. */
export const PERIODS_PER_YEAR = {
    monthly: 12,
    'semi-monthly': 24,
    biweekly: 26,
    weekly: 52,
};
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-16');
const money = z.number().int().nonnegative().max(1_000_000_000_000);
export const createCompensationSchema = z.object({
    userId: objectId,
    effectiveDate: dateOnly,
    annualAmount: money,
    payFrequency: z.enum(PAY_FREQUENCIES).default('monthly'),
    reason: z.string().trim().max(200).optional(),
    note: z.string().trim().max(1000).optional(),
});
/* ------------------------------- pay runs -------------------------------- */
/**
 * `draft` is arithmetic, `approved` is a decision, `paid` is a fact about the
 * bank. Payslips are only ever shown to employees from a `paid` run: a draft
 * is a working document, and showing somebody a net figure that later changes
 * is worse than showing them nothing.
 */
export const PAY_RUN_STATUSES = ['draft', 'approved', 'paid'];
export const createPayRunSchema = z.object({
    period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use a month like 2026-09'),
    payDate: dateOnly,
    note: z.string().trim().max(500).optional(),
    /**
     * Fill the run from the compensation in force on the pay date. Off gives an
     * empty run to build by hand, which is what a bonus-only run is.
     */
    prefill: z.boolean().default(true),
});
export const payRunLineSchema = z.object({
    userId: objectId,
    gross: money,
    additions: money.default(0),
    deductions: money.default(0),
    note: z.string().trim().max(300).optional(),
});
export const updatePayRunSchema = z.object({
    payDate: dateOnly.optional(),
    note: z.string().trim().max(500).optional(),
    /** Replaces the whole set of lines: a partial patch of a payroll table is
        how two people editing it produce a run that pays somebody twice. */
    lines: z.array(payRunLineSchema).max(500).optional(),
});
export const payRunQuerySchema = z.object({
    status: z.enum(PAY_RUN_STATUSES).optional(),
    year: z.string().regex(/^\d{4}$/).optional(),
});
export const netOf = (line) => Math.max(0, line.gross + line.additions - line.deductions);
/** The gross for one period at an annual rate, rounded to the minor unit. */
export const periodGross = (annualAmount, frequency) => Math.round(annualAmount / PERIODS_PER_YEAR[frequency]);
